/**
 * Professional hospital letterhead PDFs — data auto-loaded from onboarding / settings.
 * Top HMS style: logo monogram, tagline, reg no, accent bar, document header.
 */

import { resolveHospitalForLetterhead, isLetterheadOnPdf } from './hospitalProfile';
import { MED_INSTRUCTION_SAMPLES } from '../config/emrSettings';

export function formatHospitalAddress(hospital) {
  if (!hospital) return '';
  if (typeof hospital === 'string') return hospital;
  const line3 = [hospital.city, hospital.state, hospital.pincode].filter(Boolean).join(', ');
  return [hospital.addressLine1, hospital.addressLine2, line3].filter(Boolean).join('\n');
}

export function hospitalName(hospital) {
  if (!hospital) return 'Hospital';
  if (typeof hospital === 'string') return hospital;
  return hospital.name || 'Hospital';
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

function normalizeHospital(hospital, user) {
  return resolveHospitalForLetterhead(hospital, user);
}

function contactLine(h) {
  const bits = [];
  if (h.phone) bits.push(`Tel: ${h.phone}`);
  if (h.email) bits.push(h.email);
  if (h.regNo) bits.push(`Reg. No: ${h.regNo}`);
  if (h.gstin) bits.push(`GSTIN: ${h.gstin}`);
  return bits.join(' · ');
}

function documentHeaderBlock(hospital, docTitle, docSubtitle, docRef, { showLogo = true } = {}) {
  if (!isLetterheadOnPdf(hospital)) {
    return `
    <header class="plain-header">
      <div class="plain-left">
        <h2 class="plain-doc-title">${escapeHtml(docTitle)}</h2>
        ${docSubtitle ? `<div class="plain-doc-sub">${escapeHtml(docSubtitle)}</div>` : ''}
        ${docRef ? `<div class="plain-doc-ref">${escapeHtml(docRef)}</div>` : ''}
      </div>
      <div class="plain-hosp">${escapeHtml(hospitalName(hospital))}</div>
    </header>
    <div class="plain-rule"></div>`;
  }
  return letterheadBlock(hospital, docTitle, docSubtitle, docRef, { showLogo });
}

function documentFooterBlock(h, dateStr) {
  if (!isLetterheadOnPdf(h)) {
    return `
      <div class="doc-footer doc-footer-plain">
        <span>${escapeHtml(hospitalName(h))}</span>
        <span>${escapeHtml(dateStr)}</span>
      </div>`;
  }
  return `
      <div class="doc-footer">
        <div><strong>${escapeHtml(hospitalName(h))}</strong>${formatHospitalAddress(h) ? `<br/>${escapeHtml(formatHospitalAddress(h).split('\n')[0])}` : ''}</div>
        <div>Printed ${escapeHtml(dateStr)}</div>
      </div>`;
}

function letterheadBlock(hospital, docTitle, docSubtitle, docRef, { showLogo = true } = {}) {
  const h = hospital;
  const color = h.letterheadColor || '#1e40af';
  const initials = h.logoInitials || hospitalName(h).slice(0, 2).toUpperCase();
  const logoHtml = showLogo
    ? (h.logoUrl
      ? `<img src="${escapeHtml(h.logoUrl)}" alt="" class="lh-logo-img" />`
      : `<div class="lh-logo" style="background:${color}">${escapeHtml(initials)}</div>`)
    : '';

  return `
    <header class="letterhead">
      ${logoHtml}
      <div class="lh-brand">
        <h1>${escapeHtml(hospitalName(h))}</h1>
        ${h.tagline ? `<p class="lh-tagline">${escapeHtml(h.tagline)}</p>` : ''}
        ${formatHospitalAddress(h) ? `<p class="lh-addr">${formatHospitalAddress(h).replace(/\n/g, '<br/>')}</p>` : ''}
        ${contactLine(h) ? `<p class="lh-contact">${escapeHtml(contactLine(h))}</p>` : ''}
        ${h.website ? `<p class="lh-web">${escapeHtml(h.website)}</p>` : ''}
      </div>
      <div class="lh-doc">
        <div class="lh-doc-title" style="color:${color}">${escapeHtml(docTitle)}</div>
        ${docSubtitle ? `<div class="lh-doc-sub">${escapeHtml(docSubtitle)}</div>` : ''}
        ${docRef ? `<div class="lh-doc-ref">${escapeHtml(docRef)}</div>` : ''}
      </div>
    </header>
    <div class="lh-rule" style="background:${color}"></div>`;
}

const DOC_STYLES = `
  @page { margin: 12mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; padding: 0; margin: 0; color: #0f172a; font-size: 13px; line-height: 1.5; }
  .page { max-width: 210mm; margin: 0 auto; padding: 6mm 8mm; }
  .letterhead { display: grid; grid-template-columns: 56px 1fr auto; gap: 14px; align-items: start; }
  .lh-logo { width: 52px; height: 52px; border-radius: 10px; color: #fff; font-weight: 800; font-size: 18px; display: flex; align-items: center; justify-content: center; letter-spacing: -0.02em; }
  .lh-logo-img { width: 52px; height: 52px; object-fit: contain; border-radius: 8px; }
  .lh-brand h1 { margin: 0; font-size: 20px; font-weight: 800; color: #0f172a; letter-spacing: -0.03em; line-height: 1.2; }
  .lh-tagline { margin: 2px 0 0; font-size: 11px; color: #64748b; font-style: italic; }
  .lh-addr { margin: 6px 0 0; color: #475569; font-size: 11px; line-height: 1.45; }
  .lh-contact, .lh-web { margin: 3px 0 0; color: #64748b; font-size: 10px; }
  .lh-doc { text-align: right; min-width: 120px; }
  .lh-doc-title { font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; }
  .lh-doc-sub { font-size: 10px; color: #64748b; margin-top: 3px; }
  .lh-doc-ref { font-size: 10px; color: #94a3b8; margin-top: 4px; font-family: monospace; }
  .lh-rule { height: 3px; border-radius: 2px; margin: 10px 0 16px; }
  .plain-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 4px; }
  .plain-doc-title { margin: 0; font-size: 16px; font-weight: 700; color: #0f172a; text-transform: uppercase; letter-spacing: 0.04em; }
  .plain-doc-sub { font-size: 10px; color: #64748b; margin-top: 2px; }
  .plain-doc-ref { font-size: 10px; color: #94a3b8; margin-top: 3px; font-family: monospace; }
  .plain-hosp { font-size: 11px; color: #64748b; text-align: right; max-width: 40%; }
  .plain-rule { height: 1px; background: #e2e8f0; margin: 8px 0 14px; }
  .doc-footer-plain { font-size: 9px; color: #94a3b8; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; margin-bottom: 16px; }
  .meta-grid dt { font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; margin: 0; }
  .meta-grid dd { margin: 0 0 6px; font-weight: 600; font-size: 13px; }
  table.data { width: 100%; border-collapse: collapse; margin: 10px 0; }
  table.data th, table.data td { border: 1px solid #e2e8f0; padding: 7px 10px; text-align: left; font-size: 12px; }
  table.data th { background: #f8fafc; font-size: 10px; text-transform: uppercase; color: #64748b; }
  .amount-box { text-align: right; margin-top: 14px; padding: 12px 14px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; font-size: 16px; font-weight: 700; }
  .amount-box span { color: #059669; font-size: 20px; }
  .bill-totals { margin: 14px 0 0 auto; width: 260px; font-size: 12px; }
  .bill-totals > div { display: flex; justify-content: space-between; padding: 4px 0; }
  .bill-grand { border-top: 1px solid #cbd5e1; margin-top: 4px; padding-top: 8px !important; font-weight: 700; font-size: 15px; }
  .bill-grand span:last-child { color: #059669; }
  .doc-footer { margin-top: 32px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; align-items: flex-end; }
  .doc-footer strong { color: #64748b; display: block; font-size: 10px; }
  .sign { margin-top: 36px; text-align: right; }
  .sign .line { border-top: 1px solid #cbd5e1; display: inline-block; min-width: 200px; padding-top: 8px; font-weight: 600; font-size: 12px; }
  .rx { white-space: pre-wrap; line-height: 1.75; font-size: 13px; margin: 12px 0; padding: 10px; background: #fafafa; border-left: 3px solid #e2e8f0; }
  .section-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; margin: 14px 0 6px; }
  .token-box { text-align: center; padding: 24px; border: 2px dashed #cbd5e1; border-radius: 12px; margin: 16px 0; }
  .token-num { font-size: 48px; font-weight: 900; color: #1e40af; letter-spacing: 0.08em; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

export function printHospitalDocument({
  title, docTitle, docSubtitle, docRef, bodyHtml, hospital, user,
  fontSize = 13, showLogo = true, showQr = false,
}) {
  const h = normalizeHospital(hospital, user);
  const w = window.open('', '_blank');
  if (!w) return;
  const dateStr = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  const ref = docRef || dateStr;
  const qrBlock = showQr && ref
    ? `<div style="margin-top:14px;padding:8px 10px;border:1px dashed #cbd5e1;border-radius:8px;font-size:10px;color:#64748b">Ref: ${escapeHtml(ref)}</div>`
    : '';
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
    <style>${DOC_STYLES} body { font-size: ${Number(fontSize) || 13}px; }</style></head><body>
    <div class="page">
      ${documentHeaderBlock(h, docTitle, docSubtitle, ref, { showLogo })}
      ${bodyHtml}
      ${qrBlock}
      ${documentFooterBlock(h, dateStr)}
    </div>
    <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
    </body></html>`);
  w.document.close();
}

export function printOpReceipt(receipt, hospital, user) {
  const body = `
    <dl class="meta-grid">
      <div><dt>Receipt #</dt><dd>${escapeHtml(receipt.receiptId)}</dd></div>
      <div><dt>Date &amp; time</dt><dd>${escapeHtml((receipt.createdAt || new Date().toISOString()).slice(0, 16).replace('T', ' '))}</dd></div>
      <div><dt>Patient name</dt><dd>${escapeHtml(receipt.patientName || receipt.patient)}</dd></div>
      <div><dt>UHID / ID</dt><dd>${escapeHtml(receipt.patientId || '—')}</dd></div>
      <div><dt>Service</dt><dd>${escapeHtml(receipt.service || 'Consultation')}</dd></div>
      <div><dt>Payment mode</dt><dd>${escapeHtml(receipt.paymentMode || receipt.mode || 'Cash')}</dd></div>
      ${receipt.doctor ? `<div><dt>Consultant</dt><dd>${escapeHtml(receipt.doctor)}</dd></div>` : ''}
    </dl>
    <div class="amount-box">Amount received: <span>${formatInr(receipt.amount || receipt.total)}</span></div>
    <p style="font-size:11px;color:#64748b;margin-top:12px">This receipt is valid for OP consultation. Please preserve for your records.</p>
    <div class="sign"><div class="line">Cashier / Authorized signatory</div></div>`;
  printHospitalDocument({
    title: `Receipt ${receipt.receiptId}`,
    docTitle: 'Payment Receipt',
    docSubtitle: 'Out-patient billing',
    docRef: receipt.receiptId,
    bodyHtml: body,
    hospital,
    user,
  });
}

/** Appointment slip with date, time, doctor */
export function printAppointmentSlip(queueRow, hospital, user) {
  const token = queueRow.token || queueRow.queueId?.slice(-4) || '—';
  const apptDate = queueRow.visitDate || new Date().toISOString().slice(0, 10);
  const apptTime = queueRow.appointmentTime || queueRow.time || '—';
  const body = `
    <div class="token-box">
      <div style="font-size:11px;text-transform:uppercase;color:#64748b;letter-spacing:0.1em">Appointment Slip</div>
      <div class="token-num">${escapeHtml(token)}</div>
      <div style="font-size:16px;font-weight:700;margin-top:8px">${escapeHtml(queueRow.patientName || queueRow.name)}</div>
      <div style="font-size:12px;color:#64748b;margin-top:4px">${escapeHtml(queueRow.doctor || 'Doctor')}</div>
    </div>
    <dl class="meta-grid">
      <div><dt>Date</dt><dd>${escapeHtml(new Date(apptDate).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }))}</dd></div>
      <div><dt>Time</dt><dd>${escapeHtml(apptTime)}</dd></div>
      <div><dt>Patient ID</dt><dd>${escapeHtml(queueRow.patientId || '—')}</dd></div>
      <div><dt>Visit type</dt><dd>${escapeHtml(queueRow.visitType || 'Consultation')}</dd></div>
      <div><dt>Fee</dt><dd>${formatInr(queueRow.fee)}</dd></div>
      <div><dt>Phone</dt><dd>${escapeHtml(queueRow.patientPhone || '—')}</dd></div>
    </dl>
    ${queueRow.reason ? `<p style="font-size:12px"><strong>Reason:</strong> ${escapeHtml(queueRow.reason)}</p>` : ''}
    <p style="font-size:11px;color:#64748b;text-align:center;margin-top:16px">Please arrive 10 minutes early. Bring previous reports if any.</p>`;
  printHospitalDocument({
    title: `Appointment ${token}`,
    docTitle: 'Appointment Confirmation',
    docSubtitle: apptDate,
    docRef: `APT-${token}`,
    bodyHtml: body,
    hospital,
    user,
  });
}

export function printOpToken(queueRow, hospital, user) {
  const token = queueRow.token || queueRow.queueId?.slice(-4) || '—';
  const body = `
    <div class="token-box">
      <div style="font-size:11px;text-transform:uppercase;color:#64748b;letter-spacing:0.1em">OP Token</div>
      <div class="token-num">${escapeHtml(token)}</div>
      <div style="font-size:16px;font-weight:700;margin-top:8px">${escapeHtml(queueRow.patientName || queueRow.name)}</div>
      <div style="font-size:12px;color:#64748b;margin-top:4px">${escapeHtml(queueRow.doctor || 'Doctor')} · ${escapeHtml(queueRow.status || 'Waiting')}</div>
    </div>
    <dl class="meta-grid">
      <div><dt>Patient ID</dt><dd>${escapeHtml(queueRow.patientId || '—')}</dd></div>
      <div><dt>Consult fee</dt><dd>${formatInr(queueRow.fee)}</dd></div>
    </dl>
    <p style="font-size:11px;color:#64748b;text-align:center">Please wait in the waiting area. Token displayed on OPD screen.</p>`;
  printHospitalDocument({
    title: `Token ${token}`,
    docTitle: 'OPD Token',
    docSubtitle: new Date().toLocaleDateString('en-IN'),
    docRef: `TK-${token}`,
    bodyHtml: body,
    hospital,
    user,
  });
}

export function printLabReport(order, hospital, user) {
  const h = normalizeHospital(hospital, user);
  const color = h.letterheadColor || '#1e40af';
  const initials = h.logoInitials || hospitalName(h).slice(0, 2).toUpperCase();
  const logoHtml = h.logoUrl
    ? `<img src="${escapeHtml(h.logoUrl)}" alt="" style="width:52px;height:52px;object-fit:contain;border-radius:8px;" />`
    : `<div style="width:52px;height:52px;border-radius:10px;background:${color};color:#fff;font-weight:900;font-size:18px;display:flex;align-items:center;justify-content:center;">${escapeHtml(initials)}</div>`;

  const reportDate = (order.verifiedAt || order.resultedAt || order.updatedAt || order.createdAt || new Date().toISOString());
  const reportDateStr = new Date(reportDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const reportTimeStr = new Date(reportDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  const reportId = order.reportId || order.orderId || '—';
  const patientName = order.patientName || '—';
  const age = order.age ? `${order.age} Yrs` : '—';
  const sex = order.gender || '—';
  const uhid = order.uhid || order.patientId || '—';
  const source = order.source || (order.admissionId ? 'IPD' : 'OPD');
  const consultant = order.referredBy || order.doctor || '—';
  const refBy = order.refBy || 'SELF';

  // Compute flags
  const flagFor = (value, refLow, refHigh) => {
    const v = Number(value);
    if (value === '' || value == null || Number.isNaN(v)) return '';
    const lo = refLow === '' || refLow == null ? null : Number(refLow);
    const hi = refHigh === '' || refHigh == null ? null : Number(refHigh);
    if (hi != null && !Number.isNaN(hi) && v > hi) return 'H';
    if (lo != null && !Number.isNaN(lo) && v < lo) return 'L';
    if (lo != null || hi != null) return 'N';
    return '';
  };

  const tests = Array.isArray(order.tests) && order.tests.length ? order.tests : null;

  const testGroupsHtml = tests
    ? tests.map((t) => {
        const params = t.parameters || [];
        const paramRows = params.map((p) => {
          const f = flagFor(p.value, p.refLow, p.refHigh);
          const isH = f === 'H';
          const isL = f === 'L';
          const isAbn = isH || isL;
          const ref = p.refRange || ((p.refLow != null && p.refLow !== '') || (p.refHigh != null && p.refHigh !== '')
            ? `${p.refLow ?? ''}–${p.refHigh ?? ''}` : '—');
          return `<tr style="${isAbn ? 'background:#fff5f5' : ''}">
            <td style="padding:5px 8px;border:1px solid #e2e8f0">${escapeHtml(p.name || '')}</td>
            <td style="padding:5px 8px;border:1px solid #e2e8f0;font-weight:${isAbn ? '900' : '600'};color:${isH ? '#dc2626' : isL ? '#2563eb' : '#0f172a'}">${escapeHtml(String(p.value ?? '—'))}</td>
            <td style="padding:5px 8px;border:1px solid #e2e8f0;color:#64748b">${escapeHtml(p.unit || '—')}</td>
            <td style="padding:5px 8px;border:1px solid #e2e8f0;color:#64748b">${escapeHtml(ref)}</td>
            <td style="padding:5px 8px;border:1px solid #e2e8f0;font-weight:800;color:${isH ? '#dc2626' : isL ? '#2563eb' : '#64748b'};text-align:center">${isAbn ? f : ''}</td>
          </tr>`;
        }).join('');

        return `
          <div style="background:#f1f5f9;padding:5px 10px;font-weight:800;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;color:#475569;margin-top:12px;border-left:4px solid ${color}">
            BLOOD FOR: ${escapeHtml(t.name)}
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:11.5px;margin-bottom:2px">
            <thead>
              <tr style="background:#f8fafc">
                <th style="padding:6px 8px;border:1px solid #e2e8f0;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase">Test Name</th>
                <th style="padding:6px 8px;border:1px solid #e2e8f0;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase">Findings/Units</th>
                <th style="padding:6px 8px;border:1px solid #e2e8f0;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase">Unit</th>
                <th style="padding:6px 8px;border:1px solid #e2e8f0;text-align:left;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase">Normal Values</th>
                <th style="padding:6px 8px;border:1px solid #e2e8f0;text-align:center;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase">Flag</th>
              </tr>
            </thead>
            <tbody>${paramRows || `<tr><td colspan="5" style="padding:12px;text-align:center;color:#94a3b8;border:1px solid #e2e8f0">No results entered yet.</td></tr>`}</tbody>
          </table>`;
      }).join('')
    : `<div style="padding:16px;color:#64748b;font-style:italic;border:1px solid #e2e8f0;border-radius:6px;margin:10px 0">${escapeHtml(order.result || 'Results pending verification.')}</div>`;

  const verifiedBy = order.verifiedBy || 'Lab Technician';
  const pathologist = order.pathologist || 'Consultant Pathologist\nMBBS, MD–PATH';

  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Lab Report ${reportId}</title>
  <style>
    @page { margin: 8mm 10mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #0f172a; margin: 0; }
    .page { max-width: 210mm; margin: 0 auto; padding: 4mm; }
    .lh { display: grid; grid-template-columns: 60px 1fr auto; gap: 12px; align-items: start; margin-bottom: 6px; }
    .lh h1 { margin: 0; font-size: 18px; font-weight: 900; color: ${color}; }
    .lh .tagline { font-size: 10px; color: #64748b; font-style: italic; margin: 2px 0; }
    .lh .addr { font-size: 9.5px; color: #475569; }
    .lh-right { text-align: right; font-size: 9.5px; color: #475569; line-height: 1.6; }
    .rule { height: 3px; background: ${color}; border-radius: 2px; margin: 6px 0; }
    .lab-report-title { text-align: center; font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: ${color}; border: 1.5px solid ${color}; padding: 4px; margin: 8px 0 10px; }
    .patient-box { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #cbd5e1; border-radius: 4px; margin-bottom: 12px; overflow: hidden; }
    .patient-left { padding: 8px 12px; border-right: 1px solid #cbd5e1; }
    .patient-right { padding: 8px 12px; }
    .pi { display: flex; gap: 6px; font-size: 11px; margin-bottom: 3px; }
    .pi label { color: #64748b; min-width: 80px; font-weight: 600; }
    .pi strong { color: #0f172a; font-weight: 700; }
    .abnormal-legend { display: flex; gap: 16px; font-size: 10px; margin: 6px 0 10px; color: #64748b; }
    .legend-h { color: #dc2626; font-weight: 700; }
    .legend-l { color: #2563eb; font-weight: 700; }
    .end-of-report { text-align: center; border-top: 1px dashed #cbd5e1; border-bottom: 1px dashed #cbd5e1; margin: 16px 0; padding: 4px; font-size: 10px; color: #94a3b8; letter-spacing: 0.08em; }
    .sign-grid { display: grid; grid-template-columns: 1fr 1fr; margin-top: 20px; gap: 20px; }
    .sign-block { text-align: center; }
    .sign-line { border-top: 1px solid #94a3b8; padding-top: 6px; font-size: 10px; font-weight: 700; display: inline-block; min-width: 180px; white-space: pre-line; }
    .equipment-note { margin-top: 14px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #f1f5f9; padding-top: 6px; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body><div class="page">
    <div class="lh">
      ${logoHtml}
      <div>
        <h1>${escapeHtml(hospitalName(h))}</h1>
        ${h.tagline ? `<div class="tagline">${escapeHtml(h.tagline)}</div>` : ''}
        <div class="addr">${escapeHtml(formatHospitalAddress(h).replace(/\n/g, ', '))}</div>
        <div class="addr">Tel: ${escapeHtml(h.phone || '—')} | ${escapeHtml(h.email || '')}</div>
      </div>
      <div class="lh-right">
        <div>Report Date: <strong>${escapeHtml(reportDateStr)} ${escapeHtml(reportTimeStr)}</strong></div>
        <div>Report ID: <strong>${escapeHtml(String(reportId))}</strong></div>
        <div style="margin-top:6px;padding:4px 8px;background:#f1f5f9;border:1px solid #e2e8f0;border-radius:4px;font-size:9px;letter-spacing:0.08em;">
          ||||| BARCODE ${escapeHtml(String(reportId))} |||||
        </div>
      </div>
    </div>
    <div class="rule"></div>
    <div class="lab-report-title">LAB REPORT</div>

    <div class="patient-box">
      <div class="patient-left">
        <div class="pi"><label>Name :</label><strong>Mrs. ${escapeHtml(patientName)}</strong></div>
        <div class="pi"><label>Age / Gen :</label><strong>${escapeHtml(age)} / ${escapeHtml(sex)}</strong></div>
        <div class="pi"><label>UHID :</label><strong>${escapeHtml(String(uhid))}</strong></div>
        <div class="pi"><label>Source :</label><strong>${escapeHtml(source)}</strong></div>
      </div>
      <div class="patient-right">
        <div class="pi"><label>Consultant :</label><strong>Dr. ${escapeHtml(consultant)}</strong></div>
        <div class="pi"><label>Ref. By :</label><strong>${escapeHtml(refBy)}</strong></div>
        <div class="pi"><label>Date :</label><strong>${escapeHtml(reportDateStr)}</strong></div>
        ${order.amount ? `<div class="pi"><label>Lab Charges :</label><strong>${formatInr(order.amount)}</strong></div>` : ''}
      </div>
    </div>

    <div class="abnormal-legend">
      <span>Flags:</span>
      <span class="legend-h">H = High (above normal range)</span>
      <span class="legend-l">L = Low (below normal range)</span>
    </div>

    ${testGroupsHtml}

    <div class="end-of-report">- - - - - - End of Report - - - - - -</div>

    <div class="sign-grid">
      <div class="sign-block">
        <div class="sign-line">${escapeHtml(verifiedBy)}\nLab Technician</div>
      </div>
      <div class="sign-block">
        <div class="sign-line">${escapeHtml(pathologist)}</div>
      </div>
    </div>

    <div class="equipment-note">
      X-RAY · ECG · ERBA (CHEM-7) ANALYSER · SPECIAL VISIT AVAILABLE 24 HOURS SERVICE
    </div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`);
  w.document.close();
}

export function printDischargeSummary(admission, hospital, user) {
  const body = `
    <dl class="meta-grid">
      <div><dt>IP ID</dt><dd>${escapeHtml(admission.admissionId || admission.ipid)}</dd></div>
      <div><dt>Discharge date</dt><dd>${escapeHtml((admission.dischargedAt || new Date().toISOString()).slice(0, 10))}</dd></div>
      <div><dt>Patient</dt><dd>${escapeHtml(admission.patientName || admission.name)}</dd></div>
      <div><dt>Bed / Ward</dt><dd>${escapeHtml(admission.bedLabel || admission.bed || '—')} · ${escapeHtml(admission.ward || '—')}</dd></div>
      <div><dt>Consultant</dt><dd>${escapeHtml(admission.doctor || '—')}</dd></div>
      <div><dt>Insurance</dt><dd>${escapeHtml(admission.insurance || 'Cash')}</dd></div>
      <div><dt>Admitted on</dt><dd>${escapeHtml((admission.admittedAt || '').slice(0, 10))}</dd></div>
      <div><dt>Admission reason</dt><dd>${escapeHtml(admission.reason || '—')}</dd></div>
    </dl>
    <div class="section-title">Final bill summary</div>
    ${(() => {
      const total = admission.finalBill ?? admission.pendingBill ?? admission.pending ?? 0;
      const charges = Array.isArray(admission.charges) ? admission.charges : [];
      const chargeSum = charges.reduce((s, c) => s + Number(c.amount || 0), 0);
      const roomAccrued = Math.max(0, Number(total) - chargeSum);
      return `<table class="data">
        <tr><th>Description</th><th style="text-align:right">Amount (₹)</th></tr>
        <tr><td>Room, nursing &amp; bed charges</td><td style="text-align:right">${formatInr(roomAccrued).replace('₹', '')}</td></tr>
        ${charges.map((c) => `<tr><td>${escapeHtml(c.category ? `${c.category} — ` : '')}${escapeHtml(c.desc || '')}${c.qty > 1 ? ` ×${c.qty}` : ''}</td><td style="text-align:right">${formatInr(c.amount).replace('₹', '')}</td></tr>`).join('')}
        <tr><th>Total payable</th><th style="text-align:right">${formatInr(total).replace('₹', '')}</th></tr>
      </table>`;
    })()}
    <p style="font-size:11px;color:#64748b">Follow-up as advised by treating doctor. Present this at billing for final settlement if due.</p>
    <div class="sign"><div class="line">Discharge authorized — Hospital administration</div></div>`;
  printHospitalDocument({
    title: `Discharge ${admission.admissionId}`,
    docTitle: 'Discharge Summary',
    docSubtitle: 'In-patient department',
    docRef: admission.admissionId,
    bodyHtml: body,
    hospital,
    user,
  });
}

export function printPrescription(form, doctor, hospital, user, emrSettings = {}) {
  const printSettings = emrSettings.printSettings || {};
  const rxTpl = emrSettings.rxPrintTemplate || {};
  const medLang = printSettings.medInstructionLang || 'hi';
  const medSample = MED_INSTRUCTION_SAMPLES[medLang] || MED_INSTRUCTION_SAMPLES.hi;
  const dosing = (l) => {
    const hasMan = [l.m, l.a, l.n].some((x) => x !== '' && x != null);
    return hasMan ? `${Number(l.m || 0)}-${Number(l.a || 0)}-${Number(l.n || 0)}` : (l.frequency || '');
  };
  const lines = (form.rxLines || []).filter((l) => l.drug);
  const rxHtml = lines.length
    ? `<table class="data">
        <tr><th>#</th><th>Medicine</th><th>Dose</th><th>M-A-N</th><th>When</th><th>Duration</th><th>Instructions</th></tr>
        ${lines.map((l, i) => `<tr>
          <td>${i + 1}</td>
          <td>${escapeHtml(l.drug)}${l.generic ? `<br><span style="font-size:9px;color:#94a3b8">${escapeHtml(l.generic)}</span>` : ''}</td>
          <td>${escapeHtml(l.dose || '—')}</td>
          <td>${escapeHtml(dosing(l) || '—')}</td>
          <td>${escapeHtml(l.food || l.route || '—')}</td>
          <td>${escapeHtml(l.duration || '—')}</td>
          <td>${escapeHtml(l.instructions || '')}</td>
        </tr>`).join('')}
      </table>`
    : '<div class="rx">—</div>';
  const body = `
    <dl class="meta-grid">
      <div><dt>Patient</dt><dd>${escapeHtml(form.patientName)}</dd></div>
      <div><dt>Patient ID</dt><dd>${escapeHtml(form.patientId || '—')}</dd></div>
      <div><dt>Age / Sex</dt><dd>${escapeHtml([form.patientAge, form.patientGender].filter(Boolean).join(' / ') || '—')}</dd></div>
      <div><dt>Date</dt><dd>${escapeHtml(new Date().toLocaleDateString('en-IN'))}</dd></div>
    </dl>
    ${form.complaint ? `<p><strong>Chief complaint:</strong> ${escapeHtml(form.complaint)}</p>` : ''}
    ${form.assessment?.primary ? `<p><strong>Provisional diagnosis:</strong> ${escapeHtml(form.assessment.primary)}</p>` : ''}
    <div class="section-title">℞ Prescription</div>
    ${rxHtml}
    ${(form.labOrders || []).length ? `<div class="section-title">Investigations advised</div><div class="rx">${escapeHtml((form.labOrders || []).join(', '))}</div>` : ''}
    ${form.plan?.advice ? `<p><strong>Patient advice:</strong> ${escapeHtml(form.plan.advice)}</p>` : ''}
    ${printSettings.bilingualAdvice && medSample ? `<p style="color:#0d6e6a"><strong>सलाह / Advice:</strong> ${escapeHtml(medSample)}</p>` : ''}
    ${form.plan?.followUp ? `<p><strong>Follow-up:</strong> ${escapeHtml(form.plan.followUp)}</p>` : ''}
    ${rxTpl.footerText ? `<p class="hint" style="margin-top:12px">${escapeHtml(rxTpl.footerText)}</p>` : ''}
    <div class="sign"><div class="line">${escapeHtml(doctor?.name || 'Doctor')}${rxTpl.showDoctorReg !== false && user?.regNo ? `<br/><span style="font-weight:400;font-size:10px">Reg. ${escapeHtml(user.regNo)}</span>` : ''}<br/><span style="font-weight:400;font-size:10px">Reg. Medical Practitioner</span></div></div>`;
  printHospitalDocument({
    title: `Rx ${form.patientName}`,
    docTitle: 'Prescription',
    docSubtitle: doctor?.name || 'OPD',
    docRef: form.patientId || form.recordId || '',
    bodyHtml: body,
    hospital,
    user,
    fontSize: printSettings.fontSize || 13,
    showQr: (printSettings.showQr !== false) && (rxTpl.showQr !== false),
    showLogo: rxTpl.showLogo !== false,
  });
}

export function printMedicalCertificate(cert, hospital, user) {
  const restLine = cert.toDate
    ? `from <strong>${escapeHtml(cert.fromDate)}</strong> to <strong>${escapeHtml(cert.toDate)}</strong>`
    : `for <strong>${cert.days || '—'}</strong> day(s) from <strong>${escapeHtml(cert.fromDate)}</strong>`;
  const body = `
    <p style="line-height:1.8;font-size:14px">
      This is to certify that <strong>${escapeHtml(cert.patientName)}</strong>
      ${cert.patientId ? `(UHID: ${escapeHtml(cert.patientId)})` : ''}
      ${cert.age ? `, aged ${escapeHtml(String(cert.age))} years` : ''}
      ${cert.gender ? `, ${escapeHtml(cert.gender)}` : ''},
      ${cert.certType?.toLowerCase().includes('fitness')
        ? 'has been examined and found <strong>fit</strong> for work / travel.'
        : `is suffering from <strong>${escapeHtml(cert.diagnosis || '—')}</strong> and is advised rest ${restLine}.`}
    </p>
    ${cert.remarks ? `<p><strong>Remarks:</strong> ${escapeHtml(cert.remarks)}</p>` : ''}
    <div class="sign"><div class="line">${escapeHtml(user?.name || 'Doctor')}<br/><span style="font-weight:400;font-size:10px">Reg. Medical Practitioner</span></div></div>`;
  printHospitalDocument({
    title: `Certificate ${cert.patientName}`,
    docTitle: cert.certType || 'Medical Certificate',
    docSubtitle: new Date().toLocaleDateString('en-IN'),
    docRef: cert.patientId || '',
    bodyHtml: body,
    hospital,
    user,
  });
}

export function printPharmacyBill(order, hospital, user) {
  const h = normalizeHospital(hospital, user);
  const color = h.letterheadColor || '#1e40af';
  const initials = h.logoInitials || hospitalName(h).slice(0, 2).toUpperCase();
  const logoHtml = h.logoUrl
    ? `<img src="${escapeHtml(h.logoUrl)}" alt="" style="width:52px;height:52px;object-fit:contain;border-radius:8px;" />`
    : `<div style="width:52px;height:52px;border-radius:10px;background:${color};color:#fff;font-weight:900;font-size:18px;display:flex;align-items:center;justify-content:center;">${escapeHtml(initials)}</div>`;

  const lines = Array.isArray(order.lines) ? order.lines : [];
  const invoiceNo = order.invoiceNo || order.orderId || '—';
  const dateStr = (order.createdAt || new Date().toISOString()).slice(0, 10).split('-').reverse().join('-');
  const patientName = order.patientName || 'Walk-in';
  const doctorName = order.doctor || order.doctorName || '—';
  const address = order.patientAddress || h.city || '—';
  const ipd = order.ipNo || order.admissionId || '—';
  const paymentMode = order.paymentMode || 'Cash';

  // Calculate GST breakdown (after line discount, matching PharmacyDesk lineTotal)
  const taxLines = lines.map((l, i) => {
    const mrp = Number(l.mrp || l.rate || 0);
    const qty = Number(l.qty || 1);
    const discPct = Number(l.discPct || 0);
    const gstPct = Number(l.gstPct || 5);
    const sgstPct = gstPct / 2;
    const cgstPct = gstPct / 2;
    const gross = mrp * qty;
    const taxable = parseFloat((gross - (gross * discPct) / 100).toFixed(2));
    const sgst = parseFloat((taxable * sgstPct / 100).toFixed(2));
    const cgst = parseFloat((taxable * cgstPct / 100).toFixed(2));
    const total = parseFloat((taxable + sgst + cgst).toFixed(2));
    return { ...l, idx: i + 1, mrp, qty, discPct, gstPct, sgstPct, cgstPct, taxable, sgst, cgst, total };
  });

  const grossTaxable = taxLines.reduce((s, l) => s + l.taxable, 0);
  const totalSgst = taxLines.reduce((s, l) => s + l.sgst, 0);
  const totalCgst = taxLines.reduce((s, l) => s + l.cgst, 0);
  const computedNet = Math.round(grossTaxable + totalSgst + totalCgst);
  const netPayable = order.amount != null ? Math.round(Number(order.amount)) : computedNet;

  // Amount in words
  const toWords = (n) => {
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const fn = (num) => {
      if (num === 0) return '';
      if (num < 20) return a[num] + ' ';
      if (num < 100) return b[Math.floor(num / 10)] + ' ' + a[num % 10] + ' ';
      if (num < 1000) return a[Math.floor(num / 100)] + ' Hundred ' + fn(num % 100);
      if (num < 100000) return fn(Math.floor(num / 1000)) + 'Thousand ' + fn(num % 1000);
      if (num < 10000000) return fn(Math.floor(num / 100000)) + 'Lakh ' + fn(num % 100000);
      return fn(Math.floor(num / 10000000)) + 'Crore ' + fn(num % 10000000);
    };
    return fn(Math.abs(Math.floor(n))).trim() + ' Only';
  };

  const itemRows = taxLines.map((l) => `
    <tr>
      <td>${l.idx}</td>
      <td><strong>${escapeHtml(l.name || l.drug || '')}</strong>${l.batch ? `<br><span style="font-size:9px;color:#64748b">Batch: ${escapeHtml(l.batch)}</span>` : ''}</td>
      <td>${escapeHtml(l.batch || '—')}</td>
      <td>${escapeHtml(l.expiry || l.expiryDate || '—')}</td>
      <td style="text-align:right">${escapeHtml(String(l.hsn || '3002'))}</td>
      <td style="text-align:right">${l.mrp.toFixed(2)}</td>
      <td style="text-align:right">${l.qty}</td>
      <td style="text-align:right">${(l.sgstPct)}% (${l.sgst.toFixed(2)})</td>
      <td style="text-align:right">${(l.cgstPct)}% (${l.cgst.toFixed(2)})</td>
      <td style="text-align:right"><strong>${l.total.toFixed(2)}</strong></td>
    </tr>`).join('');

  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pharmacy Tax Invoice ${invoiceNo}</title>
  <style>
    @page { margin: 8mm 10mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #0f172a; margin: 0; }
    .page { max-width: 210mm; margin: 0 auto; padding: 4mm; }
    .lh { display: grid; grid-template-columns: 60px 1fr; gap: 12px; align-items: center; margin-bottom: 6px; }
    .lh h1 { margin: 0; font-size: 18px; font-weight: 900; color: ${color}; }
    .lh .tagline { font-size: 10px; color: #64748b; font-style: italic; margin: 1px 0; }
    .lh .addr { font-size: 9.5px; color: #475569; margin: 2px 0; }
    .rule { height: 3px; background: ${color}; border-radius: 2px; margin: 6px 0; }
    .doc-title { text-align: center; font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: ${color}; margin: 6px 0 10px; border: 1px solid ${color}; padding: 4px; }
    .meta { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px; margin-bottom: 10px; font-size: 11px; }
    .meta-row { display: flex; gap: 4px; }
    .meta-row label { color: #64748b; white-space: nowrap; }
    .meta-row strong { font-weight: 700; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    th, td { border: 1px solid #cbd5e1; padding: 4px 6px; text-align: left; }
    th { background: #f1f5f9; font-weight: 700; font-size: 9px; text-transform: uppercase; color: #475569; }
    .totals { margin-top: 8px; display: grid; grid-template-columns: 1fr auto; gap: 0; }
    .totals-table { margin-left: auto; min-width: 240px; font-size: 11px; }
    .totals-table td { border: 1px solid #e2e8f0; padding: 3px 8px; }
    .totals-table .grand { background: #f0fdf4; font-weight: 800; font-size: 13px; }
    .words { font-size: 10px; color: #475569; margin: 8px 0; font-style: italic; }
    .tc { font-size: 9px; color: #64748b; margin-top: 10px; }
    .sign-row { display: grid; grid-template-columns: 1fr 1fr; margin-top: 20px; }
    .sign-box { text-align: right; }
    .sign-line { border-top: 1px solid #94a3b8; padding-top: 4px; font-size: 10px; font-weight: 700; display: inline-block; min-width: 160px; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body><div class="page">
    <div class="lh">
      ${logoHtml}
      <div>
        <h1>${escapeHtml(hospitalName(h))}</h1>
        ${h.tagline ? `<div class="tagline">${escapeHtml(h.tagline)}</div>` : ''}
        <div class="addr">${escapeHtml(formatHospitalAddress(h).replace(/\n/g, ', '))}</div>
        <div class="addr">Tel: ${escapeHtml(h.phone || '—')} | Email: ${escapeHtml(h.email || '—')}</div>
        ${h.gstin ? `<div class="addr">GSTIN: ${escapeHtml(h.gstin)}</div>` : ''}
      </div>
    </div>
    <div class="rule"></div>
    <div class="doc-title">Pharmacy Tax Invoice</div>

    <div class="meta">
      <div>
        <div class="meta-row"><label>Invoice No. :</label> <strong>${escapeHtml(invoiceNo)}</strong></div>
        <div class="meta-row"><label>Date :</label> <strong>${escapeHtml(dateStr)}</strong></div>
        <div class="meta-row"><label>Contact :</label> <strong>${escapeHtml(order.patientPhone || '—')}</strong></div>
        <div class="meta-row"><label>Panel/Tpa :</label> <strong>${escapeHtml(order.insurance || 'NA')}</strong></div>
      </div>
      <div>
        <div class="meta-row"><label>Consultant :</label> <strong>Dr. ${escapeHtml(doctorName)}</strong></div>
        <div class="meta-row"><label>IPD :</label> <strong>${escapeHtml(String(ipd))}</strong></div>
      </div>
      <div>
        <div class="meta-row"><label>Patient :</label> <strong>${escapeHtml(patientName)}</strong></div>
        <div class="meta-row"><label>Address :</label> <strong>${escapeHtml(address)}</strong></div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>S.No</th>
          <th>Medicine(s)</th>
          <th>Batch</th>
          <th>Expiry</th>
          <th style="text-align:right">HSN</th>
          <th style="text-align:right">MRP</th>
          <th style="text-align:right">Qty.</th>
          <th style="text-align:right">SGST</th>
          <th style="text-align:right">CGST</th>
          <th style="text-align:right">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows || `<tr><td colspan="10" style="text-align:center;color:#94a3b8;padding:12px">${escapeHtml(order.items || 'No items')}</td></tr>`}
        <tr style="background:#f8fafc">
          <td colspan="9" style="text-align:right;font-weight:700">Net Payable Amount (₹)</td>
          <td style="text-align:right;font-weight:900;font-size:13px;color:${color}">${netPayable.toFixed(2)}</td>
        </tr>
      </tbody>
    </table>

    <div class="totals">
      <div>
        <div class="words">Rupees ${escapeHtml(toWords(netPayable))} (Inclusive of all taxes)</div>
        <div class="words">Payment Mode: ${escapeHtml(paymentMode)} (User: Pharma)</div>
        <div class="tc">
          <strong>Terms &amp; Conditions:</strong><br>
          1. Medicine's GST may vary with their HSN codes<br>
          2. Medicine without bill will not be taken back or exchanged.
        </div>
      </div>
      <div>
        <table class="totals-table">
          <tr><td>Gross Taxable Amount</td><td style="text-align:right">${grossTaxable.toFixed(2)}</td></tr>
          <tr><td>SGST</td><td style="text-align:right">${totalSgst.toFixed(2)}</td></tr>
          <tr><td>CGST</td><td style="text-align:right">${totalCgst.toFixed(2)}</td></tr>
          <tr><td>Discount</td><td style="text-align:right">0.00</td></tr>
          <tr><td>Round / Waive Off</td><td style="text-align:right">0.00</td></tr>
          <tr class="grand"><td>Net Payable Amount (₹)</td><td style="text-align:right">₹${netPayable.toFixed(2)}</td></tr>
        </table>
      </div>
    </div>

    <div class="sign-row">
      <div style="font-size:10px;color:#64748b">Generated by ${escapeHtml(hospitalName(h))} HMS · ${new Date().toLocaleString('en-IN')}</div>
      <div class="sign-box"><div class="sign-line">Authorized Signatory</div></div>
    </div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`);
  w.document.close();
}

/* ── IPD Investigation Request Slip (matches real hospital format) ── */
export function printInvestigationSlip(order, admission, hospital, user) {
  const h = normalizeHospital(hospital, user);
  const color = h.letterheadColor || '#1e40af';
  const initials = h.logoInitials || hospitalName(h).slice(0, 2).toUpperCase();
  const logoHtml = h.logoUrl
    ? `<img src="${escapeHtml(h.logoUrl)}" alt="" style="width:52px;height:52px;object-fit:contain;border-radius:8px;" />`
    : `<div style="width:52px;height:52px;border-radius:10px;background:${color};color:#fff;font-weight:900;font-size:18px;display:flex;align-items:center;justify-content:center;">${escapeHtml(initials)}</div>`;

  const tests = Array.isArray(order.tests) ? order.tests : [{ name: order.test || '—', rate: order.amount || 0, unit: 1 }];
  const totalAmt = tests.reduce((s, t) => s + Number(t.rate || t.amount || 0), 0) || Number(order.amount || 0);
  const patientName = order.patientName || admission?.patientName || '—';
  const uhid = order.uhid || order.patientId || admission?.patientId || '—';
  const ipNo = order.admissionId || admission?.admissionId || '—';
  const dateStr = (order.createdAt || new Date().toISOString()).slice(0, 10).split('-').reverse().join('-');
  const reqNo = order.orderId || order.requestNo || '—';
  const department = order.department || 'Diagnostic Unit';
  const allocation = order.allocation || order.ward || '—';
  const payMode = order.paymentMode || 'Online PhonePay';
  const receiptNo = order.receiptNo || '—';

  const testRows = tests.map((t, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td style="text-align:center">${escapeHtml(t.code || t.name?.slice(0, 3).toUpperCase() || '—')}</td>
      <td>${escapeHtml(t.name || '—')}<br><span style="font-size:9px;color:#64748b">(${escapeHtml(department.toUpperCase())})</span></td>
      <td style="text-align:right">${Number(t.rate || t.amount || 0).toLocaleString('en-IN')}</td>
      <td style="text-align:center">${t.unit || 1}</td>
      <td style="text-align:right"><strong>${Number(t.rate || t.amount || 0).toLocaleString('en-IN')}.00</strong></td>
    </tr>`).join('');

  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Investigation Request Slip ${reqNo}</title>
  <style>
    @page { margin: 8mm 10mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #0f172a; margin: 0; }
    .page { max-width: 210mm; margin: 0 auto; padding: 4mm; }
    .lh { display: grid; grid-template-columns: 60px 1fr auto; gap: 12px; align-items: start; margin-bottom: 6px; }
    .lh h1 { margin: 0; font-size: 18px; font-weight: 900; color: ${color}; }
    .lh .tagline { font-size: 10px; color: #64748b; font-style: italic; }
    .lh .addr { font-size: 9.5px; color: #475569; margin-top: 2px; }
    .lh-right { text-align: right; font-size: 9.5px; color: #475569; }
    .rule { height: 3px; background: ${color}; border-radius: 2px; margin: 6px 0; }
    .doc-title { text-align: center; font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em; color: ${color}; margin: 8px 0; }
    .patient-info { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; font-size: 11px; margin-bottom: 10px; background: #f8fafc; padding: 8px; border-radius: 6px; }
    .pi-row { display: flex; gap: 4px; }
    .pi-row label { color: #64748b; min-width: 90px; }
    .pi-row strong { font-weight: 700; }
    .slip-meta { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 4px; font-size: 11px; margin-bottom: 10px; }
    .sm-row { display: flex; gap: 4px; }
    .sm-row label { color: #64748b; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th, td { border: 1px solid #cbd5e1; padding: 5px 8px; }
    th { background: #f1f5f9; font-weight: 700; font-size: 10px; color: #475569; text-align: left; }
    .payment-box { margin-top: 14px; background: #f0fdf4; border: 1px solid #86efac; border-radius: 8px; padding: 10px 14px; }
    .payment-box p { margin: 0; font-size: 12px; }
    .payment-box strong { font-size: 15px; color: #059669; }
    .sign-row { display: grid; grid-template-columns: 1fr 1fr; margin-top: 24px; }
    .sign-line { border-top: 1px solid #94a3b8; padding-top: 4px; font-size: 10px; font-weight: 700; display: inline-block; min-width: 160px; text-align: center; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style></head><body><div class="page">
    <div class="lh">
      ${logoHtml}
      <div>
        <h1>${escapeHtml(hospitalName(h))}</h1>
        ${h.tagline ? `<div class="tagline">${escapeHtml(h.tagline)}</div>` : ''}
        <div class="addr">${escapeHtml(formatHospitalAddress(h).replace(/\n/g, ', '))}</div>
      </div>
      <div class="lh-right">
        <div>${escapeHtml(h.phone || '')}</div>
        <div>${escapeHtml(h.email || '')}</div>
      </div>
    </div>
    <div class="rule"></div>
    <div class="doc-title">IPD Investigation(s) Request Slip</div>

    <div class="patient-info">
      <div class="pi-row"><label>Name :</label> <strong>Mrs. ${escapeHtml(patientName)}</strong></div>
      <div class="pi-row"><label>IPD No. :</label> <strong>${escapeHtml(String(ipNo))}</strong></div>
      <div class="pi-row"><label>Age/DOB :</label> <strong>${escapeHtml(String(order.age || admission?.age || '—'))} /</strong></div>
      <div class="pi-row"><label>UHID :</label> <strong>${escapeHtml(String(uhid))}</strong></div>
      <div class="pi-row"><label>Gen/Mobile No. :</label> <strong>${escapeHtml(order.gender || admission?.gender || 'Female')} / ${escapeHtml(order.patientPhone || admission?.phone || '—')}</strong></div>
      <div class="pi-row"><label>Request No :</label> <strong>${escapeHtml(String(reqNo))}</strong></div>
      <div class="pi-row"><label>Address :</label> <strong>${escapeHtml(order.patientAddress || admission?.address || '—')}</strong></div>
      <div class="pi-row"><label>Date :</label> <strong>${escapeHtml(dateStr)}</strong></div>
    </div>

    <div class="slip-meta">
      <div class="sm-row"><label>Department :</label> <strong>${escapeHtml(department.toUpperCase())}</strong></div>
      <div class="sm-row"><label>Allocation :</label> <strong>${escapeHtml(allocation)}</strong></div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="text-align:center">S. No</th>
          <th style="text-align:center">Code</th>
          <th>Particular</th>
          <th style="text-align:right">Rate (Rs)</th>
          <th style="text-align:center">Unit</th>
          <th style="text-align:right">Amount(Rs)</th>
        </tr>
      </thead>
      <tbody>
        ${testRows}
        <tr style="background:#f8fafc">
          <td colspan="5" style="text-align:right;font-weight:700"></td>
          <td style="text-align:right;font-weight:900;font-size:13px">${totalAmt.toLocaleString('en-IN')},00</td>
        </tr>
      </tbody>
    </table>

    <div class="payment-box">
      <p>Received with thanks <strong>Rs.${totalAmt.toLocaleString('en-IN')}/-</strong> from Mrs. ${escapeHtml(patientName)}.
      (Rupees ${escapeHtml(
        (() => {
          const toWords = (n) => {
            const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
            const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
            const fn = (num) => {
              if (num === 0) return ''; if (num < 20) return a[num] + ' ';
              if (num < 100) return b[Math.floor(num / 10)] + ' ' + a[num % 10] + ' ';
              if (num < 1000) return a[Math.floor(num / 100)] + ' Hundred ' + fn(num % 100);
              if (num < 100000) return fn(Math.floor(num / 1000)) + 'Thousand ' + fn(num % 1000);
              return fn(Math.floor(num / 100000)) + 'Lakh ' + fn(num % 100000);
            };
            return fn(Math.abs(Math.floor(n))).trim() + ' Only';
          };
          return toWords(totalAmt);
        })()
      )})</p>
      <p style="margin-top:4px;font-size:11px">Payment Receipt No.: <strong>${escapeHtml(String(receiptNo))}</strong> &nbsp; Mode: <strong>${escapeHtml(payMode)}</strong></p>
    </div>

    <div class="sign-row">
      <div></div>
      <div style="text-align:right"><div class="sign-line">Authorized Signature</div></div>
    </div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
  </body></html>`);
  w.document.close();
}

export function printAnalyticsReport(stats, hospital, periodLabel, user) {
  const body = `
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:18px">
      <div style="padding:10px;background:#f8fafc;border-radius:8px"><div style="font-size:9px;color:#94a3b8;text-transform:uppercase">OP collected</div><strong>${formatInr(stats?.opReceiptsTotal)}</strong></div>
      <div style="padding:10px;background:#f8fafc;border-radius:8px"><div style="font-size:9px;color:#94a3b8;text-transform:uppercase">Receipts</div><strong>${stats?.receiptCount ?? 0}</strong></div>
      <div style="padding:10px;background:#f8fafc;border-radius:8px"><div style="font-size:9px;color:#94a3b8;text-transform:uppercase">IP outstanding</div><strong>${formatInr(stats?.ipOutstanding)}</strong></div>
      <div style="padding:10px;background:#f8fafc;border-radius:8px"><div style="font-size:9px;color:#94a3b8;text-transform:uppercase">IP admissions</div><strong>${stats?.ipAdmissions ?? 0}</strong></div>
      <div style="padding:10px;background:#f8fafc;border-radius:8px"><div style="font-size:9px;color:#94a3b8;text-transform:uppercase">Patients</div><strong>${stats?.patientCount ?? 0}</strong></div>
      <div style="padding:10px;background:#f8fafc;border-radius:8px"><div style="font-size:9px;color:#94a3b8;text-transform:uppercase">OP in queue</div><strong>${stats?.opInQueue ?? 0}</strong></div>
    </div>
    ${(stats?.timeline || []).length ? `
      <div class="section-title">OP revenue timeline</div>
      <table class="data"><tr><th>Date</th><th style="text-align:right">Amount</th></tr>
      ${stats.timeline.map((t) => `<tr><td>${escapeHtml(t.date)}</td><td style="text-align:right">${formatInr(t.amount)}</td></tr>`).join('')}
      </table>` : ''}`;
  printHospitalDocument({
    title: `Report ${periodLabel}`,
    docTitle: 'Management Report',
    docSubtitle: periodLabel,
    bodyHtml: body,
    hospital,
    user,
  });
}
